import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Manage from "@/v2-backoffice-client/components/manage"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { getCrossCustomerMembership } from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import UserTenantTypePicker from "@/v2-backoffice-client/user/tenant/type/UserTenantTypePicker"
import { useTenantUserAddMutation } from "@/v2-backoffice-client/tenant/user/add/tenant.user.add.mutation"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { DEFAULT_TENANT_USER_TYPE_CODE } from "@/v2-backoffice-client/tenant/user/tenant.user.constants"

export type TenantUserManageSearchItemProps = {
  user: UserFragment
  tenantId: number
  /** The team's customer link, for the cross-customer guard. */
  tenant?: Pick<TenantFragment, "ExternalCustomerId" | "CustomerName"> | null
  onAdded(roleCode: number): void
}

function TenantUserManageSearchItem(props: TenantUserManageSearchItemProps) {
  const { user, tenantId, tenant, onAdded } = props

  const [roleCode, setRoleCode] = useState(DEFAULT_TENANT_USER_TYPE_CODE)
  const [add, { error, loading }] = useTenantUserAddMutation()

  errorToast(error, { title: "Failed to add user to team" })

  function handleAdd() {
    add({
      variables: {
        tenantId,
        userId: user.Id,
        userTenantTypeCode: roleCode,
      },
    })
    onAdded(roleCode)
  }

  const isRemoved = user.UserStatus.Name === "Removed"
  // A user owned by another customer than the team's: allowed (one company
  // sharing content with another's user), but rare on purpose, so it takes
  // a confirmation instead of a click.
  const crossing = getCrossCustomerMembership(user, tenant)

  return (
    <Manage.Item>
      <Manage.Item.Text
        primary={formatUserName(user.FirstName, user.LastName)}
        secondary={user.Email ?? undefined}
        tag={isRemoved ? "removed" : undefined}
        warning={
          crossing
            ? `Owned by ${crossing.ownerName}, not by this team's customer ${crossing.teamCustomerName}.`
            : undefined
        }
      />
      <UserTenantTypePicker value={roleCode} onChange={setRoleCode} size="xs" />
      <ConfirmButton
        size="xs"
        variant={crossing ? "warning" : "ghost"}
        isSquare
        iconSrc={crossing ? "triangle-warning" : "plus"}
        content={
          crossing
            ? "Add a user owned by another customer to this team"
            : "Add user to team"
        }
        confirmContent={crossing ? "Add anyway?" : "Add?"}
        confirmVariant={crossing ? "warning" : undefined}
        isLoading={loading}
        onConfirm={handleAdd}
      />
    </Manage.Item>
  )
}

export default TenantUserManageSearchItem
